/**
 * The Daily log's tables (LED-1152, design "Daily log"): the Manpower table,
 * the walk cards (header, ONE Photos row, then the walk's entries) and the
 * flat TIME / TYPE / ENTRY / CREATOR table, plus the Group by walk switch.
 *
 * Every row is a link to the record (cmd-click opens it in a tab); a plain
 * click opens it in the pane beside the tables instead.
 */
import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { ArrowRight } from "lucide-react";
import type { ConsoleRow, DailyLogWalkRow } from "../api";
import { DL, FONT_MONO, projectDot } from "../tokens";
import { clockOf, plural } from "../components/chrome";
import { PhotoImage } from "../components/PhotoImage";
import {
  hasOpenManpowerConflict, manpowerFigures, manpowerTotals, walkDurationLabel,
  type DailyLogEntryRow, type DailyLogPhotosRow, type DailyLogTimelineRow, type DailyLogWalkGroup,
} from "./dailyLogModel";
import { entryKindLabel } from "./DailyLogEntryEditor";

const MONO: CSSProperties = { fontFamily: FONT_MONO, fontWeight: 700, textTransform: "uppercase" };
const CARD: CSSProperties = { border: `1px solid ${DL.hairline}`, borderRadius: 14, background: DL.card, overflow: "hidden", minWidth: 0 };
const ELLIPSIS: CSSProperties = { whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };

/** The badge column — wide enough for the longest one-entry label
 *  (`SAFETY VIOLATION`, `PHOTOS · 12`). `tests/guards/dailyLogBadgeFits`
 *  redoes the sum against the real category list. */
export const DAILY_LOG_BADGE_COLUMN = 116;
export const DAILY_LOG_BADGE_TYPE = {
  fontSize: 8.5,
  advanceRatio: 0.6,
  letterSpacing: 1,
  paddingX: 7,
} as const;

/** Grid tracks, the design's own. The Creator column exists only flat. */
export function manpowerColumns(flat: boolean): string {
  return flat
    // The company keeps room for its name and a CONFLICT tag when the detail
    // pane narrows the table; the comment is the column that gives way.
    ? "minmax(170px,1.5fr) minmax(0,1.3fr) 130px 64px 64px 64px"
    : "minmax(190px,1.6fr) minmax(0,1.4fr) 64px 64px 64px";
}
export function rowColumns(flat: boolean): string {
  return flat
    ? `58px ${DAILY_LOG_BADGE_COLUMN}px minmax(0,1fr) 150px 36px`
    : `58px ${DAILY_LOG_BADGE_COLUMN}px minmax(0,1fr) 36px`;
}

/** A person, as the design's 18px disc and name. */
export function PersonChip({ name, framed = false, testId }: { name: string; framed?: boolean; testId?: string }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map((part) => part[0]!.toUpperCase()).join("") || "?";
  return (
    <span data-testid={testId} style={{
      display: "inline-flex", alignItems: "center", gap: 6, minWidth: 0, fontSize: 12.5, color: DL.ink2, whiteSpace: "nowrap",
      ...(framed ? { height: 26, padding: "0 10px 0 4px", borderRadius: 13, border: `1px solid ${DL.control}`, fontWeight: 600, color: DL.ink } : {}),
    }}>
      <span aria-hidden style={{ flex: "none", width: 18, height: 18, borderRadius: 9, background: projectDot(name), color: DL.card, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 7, fontWeight: 800 }}>{initials}</span>
      <span style={{ ...ELLIPSIS, minWidth: 0 }}>{name}</span>
    </span>
  );
}

function Tag({ children, testId }: { children: ReactNode; testId?: string }) {
  return (
    <span data-testid={testId} style={{ ...MONO, display: "inline-flex", alignItems: "center", height: 20, padding: "0 7px", borderRadius: 5, background: DL.tagBg, fontSize: DAILY_LOG_BADGE_TYPE.fontSize, letterSpacing: `${DAILY_LOG_BADGE_TYPE.letterSpacing}px`, color: DL.ink2, width: "max-content", whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

function ConflictTag() {
  return (
    <span data-testid="manpower-conflict-tag" style={{ ...MONO, flex: "none", fontSize: 8.5, letterSpacing: "1px", color: DL.conflictInk, background: DL.conflictBg, padding: "3px 6px", borderRadius: 4, whiteSpace: "nowrap" }}>
      Conflict
    </span>
  );
}

function Arrow() {
  return (
    <span aria-hidden style={{ width: 28, height: 28, borderRadius: 14, border: `1px solid ${DL.control}`, display: "inline-flex", alignItems: "center", justifyContent: "center", justifySelf: "end" }}>
      <ArrowRight size={12} color={DL.ink} strokeWidth={2} />
    </span>
  );
}

/** A plain click opens the pane; a modified click follows the real href. */
function openHandler(onOpen: () => void) {
  return (event: MouseEvent<HTMLAnchorElement>) => {
    if (event.metaKey || event.ctrlKey || event.shiftKey) return;
    event.preventDefault();
    onOpen();
  };
}

/** The open row's ground is the stylesheet's (`.dl-row[data-selected]`). */
function rowGround(_selected: boolean): CSSProperties {
  return {};
}

// ---------------------------------------------------------------------------
// Manpower
// ---------------------------------------------------------------------------

export function ManpowerTable({
  rows, flat, selectedId, creatorOf, hrefForItem, onSelect,
}: {
  rows: ConsoleRow[];
  flat: boolean;
  selectedId: string | null;
  creatorOf: (row: ConsoleRow) => string | undefined;
  hrefForItem: (id: string) => string;
  onSelect: (id: string) => void;
}) {
  if (rows.length === 0) return null;
  const cols = manpowerColumns(flat);
  const totals = manpowerTotals(rows);
  const figure = (value: number | null, muted = false): ReactNode => (
    <span style={{ fontFamily: FONT_MONO, textAlign: "right", fontSize: 15, fontWeight: 700, color: muted ? DL.muted : DL.ink }}>{value === null ? "—" : value}</span>
  );
  return (
    <div data-testid="manpower-block" style={CARD}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "14px 16px 10px", borderBottom: `1px solid ${DL.hairline}` }}>
        <span style={{ fontSize: 15, fontWeight: 800, letterSpacing: "-0.3px" }}>Manpower</span>
        <span style={{ ...MONO, fontWeight: 400, fontSize: 9, letterSpacing: "1.2px", color: DL.label }}>{`${plural(totals.companies, "company", "companies")} on site`}</span>
      </div>
      <div data-testid="manpower-head" style={{ ...MONO, display: "grid", gridTemplateColumns: cols, gap: 12, padding: "8px 16px", fontSize: 8.5, letterSpacing: "1.2px", color: DL.label, borderBottom: `1px solid ${DL.rowLine}` }}>
        <span>Company · location</span>
        <span>Comment</span>
        {flat && <span>Creator</span>}
        <span style={{ textAlign: "right" }}>Workers</span>
        <span style={{ textAlign: "right" }}>Hrs each</span>
        <span style={{ textAlign: "right" }}>Total</span>
      </div>
      {rows.map((row) => {
        const { workers, hours, total } = manpowerFigures(row);
        const company = String(row.fieldValues?.vendor ?? "").trim() || row.title;
        const location = String(row.location?.name ?? row.fieldValues?.location ?? "").trim();
        const pending = row.reviewStatus === "pending";
        const creator = creatorOf(row);
        const selected = row.id === selectedId;
        return (
          <a
            key={row.id}
            className="kb dl-row"
            data-testid="manpower-row"
            data-item-id={row.id}
            data-selected={selected ? "true" : "false"}
            data-pending={pending ? "true" : undefined}
            href={hrefForItem(row.id)}
            onClick={openHandler(() => onSelect(row.id))}
            style={{ display: "grid", gridTemplateColumns: cols, gap: 12, alignItems: "center", padding: "14px 16px", borderBottom: `1px solid ${DL.rowLine}`, color: DL.ink, ...rowGround(selected) }}
          >
            <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
              <span style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                <span style={{ ...ELLIPSIS, fontSize: 14.5, fontWeight: 700, letterSpacing: "-0.2px" }}>{company}</span>
                {hasOpenManpowerConflict(row) && <ConflictTag />}
              </span>
              {location && <span data-testid="manpower-location" style={{ ...ELLIPSIS, fontSize: 12.5, color: DL.muted }}>{location}</span>}
            </span>
            <span style={{ ...ELLIPSIS, fontSize: 13.5, color: pending ? DL.conflictInk : DL.sub }}>
              {pending ? "Waiting in review" : String(row.fieldValues?.comments ?? "")}
            </span>
            {flat && (creator ? <PersonChip name={creator} /> : <span />)}
            {figure(workers)}
            {figure(hours, true)}
            {figure(total)}
          </a>
        );
      })}
      <div data-testid="manpower-total" style={{ display: "grid", gridTemplateColumns: cols, gap: 12, padding: "12px 16px", background: DL.totals }}>
        <span style={{ fontSize: 13.5, fontWeight: 700 }}>Total</span>
        <span style={{ fontSize: 13, color: DL.muted }}>{plural(totals.companies, "company", "companies")}</span>
        {flat && <span />}
        <span style={{ fontFamily: FONT_MONO, textAlign: "right", fontSize: 15, fontWeight: 700 }}>{totals.workers}</span>
        <span />
        <span style={{ fontFamily: FONT_MONO, textAlign: "right", fontSize: 15, fontWeight: 700 }}>{totals.total}</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Rows
// ---------------------------------------------------------------------------

/** One walk's photos, as ONE row: 92px squares that wrap onto more lines. */
function PhotosRow({
  row, flat, selected, href, onOpen,
}: {
  row: DailyLogPhotosRow;
  flat: boolean;
  selected: boolean;
  href: string;
  onOpen: () => void;
}) {
  return (
    <a
      className="kb dl-row"
      data-testid="daily-log-photos-row"
      data-visit-id={row.walk.visitId}
      data-selected={selected ? "true" : "false"}
      href={href}
      onClick={openHandler(onOpen)}
      style={{ display: "grid", gridTemplateColumns: rowColumns(flat), gap: 12, alignItems: "center", padding: "10px 16px", borderBottom: `1px solid ${DL.rowLine}`, color: DL.ink, ...rowGround(selected) }}
    >
      <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: DL.muted }}>{clockOf(new Date(row.at))}</span>
      <Tag testId="daily-log-photos-tag">{`Photos · ${row.photos.length}`}</Tag>
      <span data-testid="daily-log-photos-thumbs" style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0, flexWrap: "wrap", padding: "4px 0" }}>
        {row.photos.map((photo) => (
          <span key={photo.mediaId} data-testid="daily-log-photos-thumb" style={{ position: "relative", flex: "none", width: 92, height: 92, borderRadius: 10, overflow: "hidden", background: DL.photoGround, display: "block" }}>
            <PhotoImage src={photo.thumbnailUrl ?? photo.url ?? undefined} alt="" loading="lazy" style={{ display: "block", width: "100%", height: "100%", objectFit: "cover" }} />
          </span>
        ))}
      </span>
      {flat && (row.walk.walkerName ? <PersonChip name={row.walk.walkerName} /> : <span />)}
      <Arrow />
    </a>
  );
}

/** What an entry says under its title — its own words, one line. */
function entrySub(row: ConsoleRow): string {
  const values = row.fieldValues ?? {};
  const raw = String(values.comments ?? values.description ?? values.comment ?? row.description ?? row.summary ?? "").trim();
  return raw.replace(/\s+/g, " ");
}

function EntryRow({
  entry, flat, selected, href, onOpen,
}: {
  entry: DailyLogEntryRow;
  flat: boolean;
  selected: boolean;
  href: string;
  onOpen: () => void;
}) {
  const { row, walk } = entry;
  const sub = entrySub(row);
  const pending = row.reviewStatus === "pending";
  return (
    <a
      className="kb dl-row"
      data-testid="snapshot-row"
      data-item-id={row.id}
      data-selected={selected ? "true" : "false"}
      data-pending={pending ? "true" : undefined}
      href={href}
      onClick={openHandler(onOpen)}
      style={{ display: "grid", gridTemplateColumns: rowColumns(flat), gap: 12, alignItems: "center", padding: "12px 16px", borderBottom: `1px solid ${DL.rowLine}`, color: DL.ink, ...rowGround(selected) }}
    >
      <span style={{ fontFamily: FONT_MONO, fontSize: 11, color: DL.muted }}>{clockOf(new Date(row.createdAt))}</span>
      <Tag>{entryKindLabel(row.category)}</Tag>
      <span style={{ minWidth: 0 }}>
        <span style={{ ...ELLIPSIS, display: "block", fontSize: 14, fontWeight: 700, letterSpacing: "-0.2px" }}>{row.title}</span>
        {(sub || pending) && (
          <span style={{ ...ELLIPSIS, display: "block", fontSize: 12.5, color: pending ? DL.conflictInk : DL.sub, marginTop: 1 }}>
            {pending ? `Waiting in review${sub ? ` · ${sub}` : ""}` : sub}
          </span>
        )}
      </span>
      {flat && (walk?.walkerName ? <PersonChip name={walk.walkerName} /> : <span />)}
      <Arrow />
    </a>
  );
}

export interface DailyLogRowHandlers {
  selectedId: string | null;
  selectedPhotosWalk: string | null;
  hrefForItem: (id: string) => string;
  hrefForWalk: (visitId: string) => string;
  onSelect: (id: string) => void;
  onOpenPhotos: (visitId: string) => void;
}

function TimelineRow({ row, flat, handlers }: { row: DailyLogTimelineRow; flat: boolean; handlers: DailyLogRowHandlers }) {
  if (row.type === "photos") {
    return (
      <PhotosRow
        row={row}
        flat={flat}
        selected={handlers.selectedPhotosWalk === row.walk.visitId}
        href={handlers.hrefForWalk(row.walk.visitId)}
        onOpen={() => handlers.onOpenPhotos(row.walk.visitId)}
      />
    );
  }
  return (
    <EntryRow
      entry={row}
      flat={flat}
      selected={handlers.selectedId === row.row.id}
      href={handlers.hrefForItem(row.row.id)}
      onOpen={() => handlers.onSelect(row.row.id)}
    />
  );
}

// ---------------------------------------------------------------------------
// Grouped and flat
// ---------------------------------------------------------------------------

/** One walk: its header, its Photos row, its entries. */
export function WalkGroupCard({ group, handlers }: { group: DailyLogWalkGroup; handlers: DailyLogRowHandlers }) {
  const { walk } = group;
  return (
    <div data-testid="walk-timeline" data-visit-id={walk?.visitId ?? "none"} style={CARD}>
      <WalkHeader walk={walk} />
      {group.photos && <TimelineRow row={group.photos} flat={false} handlers={handlers} />}
      {group.entries.map((entry) => <TimelineRow key={entry.row.id} row={entry} flat={false} handlers={handlers} />)}
    </div>
  );
}

export function WalkHeader({ walk }: { walk: DailyLogWalkRow | null }) {
  const duration = walkDurationLabel(walk);
  return (
    <div data-testid="walk-timeline-head" style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderBottom: `1px solid ${DL.hairline}` }}>
      {walk?.startedAt && <span style={{ fontFamily: FONT_MONO, fontSize: 13, fontWeight: 700 }}>{clockOf(new Date(walk.startedAt))}</span>}
      <span style={{ ...ELLIPSIS, flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 700, letterSpacing: "-0.2px" }}>
        {walk ? walk.name || "Untitled walk" : "Not from a walk"}
      </span>
      {walk?.walkerName && <PersonChip name={walk.walkerName} framed testId="walk-timeline-walker" />}
      {duration && <span style={{ ...MONO, fontWeight: 400, fontSize: 9, letterSpacing: "1px", color: DL.label, whiteSpace: "nowrap" }}>{duration}</span>}
    </div>
  );
}

/** Group by walk off: one table, photos still one row per walk. */
export function FlatTable({ rows, handlers }: { rows: DailyLogTimelineRow[]; handlers: DailyLogRowHandlers }) {
  if (rows.length === 0) return null;
  return (
    <div data-testid="daily-log-flat" style={CARD}>
      <div style={{ ...MONO, display: "grid", gridTemplateColumns: rowColumns(true), gap: 12, padding: "9px 16px", fontSize: 8.5, letterSpacing: "1.2px", color: DL.label, borderBottom: `1px solid ${DL.hairline}` }}>
        <span>Time</span><span>Type</span><span>Entry</span><span>Creator</span><span />
      </div>
      {rows.map((row) => (
        <TimelineRow key={row.type === "photos" ? `photos:${row.walk.visitId}` : row.row.id} row={row} flat handlers={handlers} />
      ))}
    </div>
  );
}

/** The Group by walk switch. */
export function GroupByWalkToggle({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className="kb"
      role="switch"
      aria-checked={on}
      data-testid="group-by-walk"
      onClick={onToggle}
      style={{ display: "inline-flex", alignItems: "center", gap: 9, height: 30, padding: "0 4px 0 12px", borderRadius: 15, background: DL.card, border: `1px solid ${DL.control}`, fontSize: 13, fontWeight: 600, color: DL.ink, cursor: "pointer", whiteSpace: "nowrap" }}
    >
      Group by walk
      <span aria-hidden style={{ position: "relative", display: "block", width: 34, height: 22, borderRadius: 11, background: on ? DL.switchOn : DL.switchOff, transition: "background .18s" }}>
        <span style={{ position: "absolute", top: 2, left: on ? 14 : 2, width: 18, height: 18, borderRadius: 9, background: DL.card, boxShadow: DL.shadowSwitch, display: "block", transition: "left .18s" }} />
      </span>
    </button>
  );
}

/** The page's own hover and motion rules — states the stylesheet owns, so
 *  none of them is also set inline (an inline background outranks :hover). */
export function DailyLogStyles() {
  return (
    <style>{`
      @keyframes dlPaneIn { from { opacity: 0; transform: translateY(6px) scale(.985); } to { opacity: 1; transform: none; } }
      .dl-row { transition: background .14s ease; }
      .dl-row:hover { background: ${DL.rowHover}; }
      .dl-row[data-selected="true"], .dl-row[data-selected="true"]:hover { background: ${DL.rowSelected}; }
      .dl-row:active { transform: scale(.995); }
      .dl-icon:hover { background: ${DL.rowLine} !important; }
      .dl-delete:hover { background: ${DL.deleteHover} !important; border-color: ${DL.deleteLine} !important; }
      .dl-thumb { transition: transform .14s ease; }
      .dl-thumb:active { transform: scale(.97); }
      @media (prefers-reduced-motion: reduce) { .dl-row, .dl-thumb { transition: none; } }
    `}</style>
  );
}

/** A filter pill, the design's: 30px, ink when on, count at 60%. */
export function DailyLogFilterPill({ label, count, active, onClick, testId = "filter-chip" }: { label: string; count: number; active: boolean; onClick: () => void; testId?: string }) {
  return (
    <button
      type="button"
      className="kb chipswap"
      data-testid={testId}
      data-active={active ? "true" : "false"}
      aria-pressed={active}
      onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 30, padding: "0 12px", borderRadius: 15, background: active ? DL.ink : DL.card, border: `1px solid ${active ? DL.ink : DL.control}`, color: active ? DL.card : DL.ink, fontSize: 13, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
    >
      {label}
      <span style={{ fontFamily: FONT_MONO, fontSize: 9.5, opacity: 0.6 }}>{count}</span>
    </button>
  );
}
